import React from 'react';
import { cn } from '../../lib/utils';
import {
  GRADIENT_BASE_TONES,
  GRADIENT_ACCENT_COLORS,
  GRADIENT_POSITIONS,
  GRADIENT_GEOMETRIES,
  RECOMMENDED_ACCENTS,
  DEFAULT_GRADIENT,
  GRADIENT_FOG_LEVELS,
  buildGradientCss,
} from '../../constants/backgrounds';
import { DarkenSlider, ColorSwatchGrid, SliderRow, ToggleSwitch, PositionGrid, CollapseTransition } from '../../sdk';

/**
 * Gradient background editor.
 * Renders between Background picker and Glass Effect in the editor panel.
 *
 * Features:
 *  - Smart accent auto-matching: changing base tone updates accents to a curated pair
 *  - Manual override: user can click any accent color after auto-match
 *  - Geometry picker: radial / linear / conic
 *  - Independent darken slider (separate from image background darken)
 *
 * Props:
 *   gradient   — current gradient config object (or undefined)
 *   onChange   — (newGradient) => void
 */
export function GradientEditor({ gradient, onChange }) {
  const g = { ...DEFAULT_GRADIENT, ...gradient };
  const enabled = g.enabled;

  const update = (patch) => {
    const newG = { ...g, ...patch };

    // ── Smart auto-match: when base tone changes, update accent colors ──
    if (patch.base && patch.base !== g.base) {
      const rec = RECOMMENDED_ACCENTS[patch.base];
      if (rec) {
        newG.accent1Color = rec.accent1;
        newG.accent2Color = rec.accent2;
      }
    }

    onChange(newG);
  };

  return (
    <div className="field-container">
      {/* Header + Enable toggle */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="module-header">Gradient Background</div>
          <div className="module-desc">Use a gradient instead of an image.</div>
        </div>
        <ToggleSwitch
          checked={enabled}
          onChange={(v) => update({ enabled: v })}
        />
      </div>

      <CollapseTransition open={enabled}>
        <div className="space-y-4">
          {/* Live Preview */}
          <div
            className="h-16 rounded-xl border border-white/10 overflow-hidden"
            style={{ backgroundImage: buildGradientCss(g) }}
          />

          {/* Base Tone */}
          <ColorSwatchGrid
            label="Base Tone"
            colors={GRADIENT_BASE_TONES}
            value={g.base}
            onChange={(id) => update({ base: id })}
            variant="grid"
            columns={6}
          />

          {/* Geometry */}
          <div>
            <div className="text-[11px] text-white/55 mb-1.5">Geometry</div>
            <div className="flex gap-1.5">
              {GRADIENT_GEOMETRIES.map((geo) => (
                <button
                  key={geo.id}
                  onClick={() => update({ geometry: geo.id })}
                  className={cn(
                    'flex-1 py-1.5 rounded-lg border text-[10px] font-medium transition-all',
                    (g.geometry || 'radial') === geo.id
                      ? 'border-white/25 bg-white/10 text-white/90'
                      : 'border-white/8 bg-white/[0.02] text-white/45 hover:bg-white/[0.06] hover:text-white/65'
                  )}
                >
                  {geo.name}
                </button>
              ))}
            </div>
          </div>

          {/* Accent 1 */}
          <AccentSection
            label="Accent 1"
            color={g.accent1Color}
            intensity={g.accent1Intensity}
            position={g.accent1Position}
            onColorChange={(c) => update({ accent1Color: c })}
            onIntensityChange={(v) => update({ accent1Intensity: v })}
            onPositionChange={(p) => update({ accent1Position: p })}
          />

          {/* Accent 2 toggle + controls */}
          <div className="pt-2 border-t border-white/8">
            <label className="flex items-center justify-between cursor-pointer mb-2">
              <span className="text-[11px] text-white/55">Accent 2</span>
              <ToggleSwitch
                checked={g.accent2Enabled}
                onChange={(v) => update({ accent2Enabled: v })}
                size="sm"
              />
            </label>
            {g.accent2Enabled && (
              <AccentSection
                label={null}
                color={g.accent2Color}
                intensity={g.accent2Intensity}
                position={g.accent2Position}
                onColorChange={(c) => update({ accent2Color: c })}
                onIntensityChange={(v) => update({ accent2Intensity: v })}
                onPositionChange={(p) => update({ accent2Position: p })}
              />
            )}
          </div>

          {/* ── Darken slider (independent from image darken) ── */}
          <div className="pt-3 border-t border-white/8">
            <DarkenSlider
              label="Darken"
              value={g.darken ?? 0}
              onChange={(v) => update({ darken: v })}
            />
          </div>

          {/* ── Fog slider (white glow at bottom) ── */}
          <div className="pt-3 border-t border-white/8">
            <DarkenSlider
              label="Atmosphere"
              levels={GRADIENT_FOG_LEVELS}
              value={g.fog ?? 2}
              onChange={(v) => update({ fog: v })}
            />
          </div>
        </div>
      </CollapseTransition>
    </div>
  );
}

/* ─── Accent section (color + intensity slider + position grid) ─── */

function AccentSection({ label, color, intensity, position, onColorChange, onIntensityChange, onPositionChange }) {
  return (
    <div className="space-y-2.5">
      {label && <div className="text-[11px] text-white/55">{label}</div>}

      {/* Color palette */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-white/40 w-10 shrink-0">Color</span>
        <ColorSwatchGrid
          colors={GRADIENT_ACCENT_COLORS}
          value={color}
          onChange={onColorChange}
          variant="compact"
        />
      </div>

      {/* Intensity slider */}
      <SliderRow
        label="Glow"
        min={0}
        max={100}
        step={5}
        value={intensity}
        onChange={onIntensityChange}
        format={(v) => `${v}%`}
        labelWidth="w-10"
        valueWidth="w-8"
      />

      {/* Position grid (3×3) */}
      <PositionGrid
        positions={GRADIENT_POSITIONS}
        value={position}
        onChange={onPositionChange}
        label="Position"
        labelWidth="w-10"
      />
    </div>
  );
}
